<template>
  <VOnboardingWrapper
    ref="wrapper"
    :steps="steps"
    :options="{
      padding: 8,
      overlay: {
        enabled: true,
        padding: 8,
        borderRadius: 4,
        preventOverlayInteraction: false
      },
      scrollToStep: {
        enabled: false
      },
      hideButtons: {
        exit: false
      },
      disableScrolling: false,
      autoFinishByExit: false
    }"
    @step-change="onStepChange"
  >
    <template #default="{ previous, next, step, isFirst, isLast, index }">
      <VOnboardingStep>
        <div class="max-w-[200px] rounded-lg border border-purple-100 bg-white shadow-lg">
          <div class="p-2">
            <div class="space-y-1">
              <div v-if="step.content">
                <h3
                  v-if="step.content.title"
                  class="text-xs font-medium leading-4 text-gray-900"
                >
                  {{ step.content.title }}
                </h3>
                <div
                  v-if="step.content.description"
                  class="mt-1 text-[11px] leading-4 text-gray-600"
                >
                  <p>{{ step.content.description }}</p>
                </div>
              </div>
              <div class="flex items-center justify-between pt-1">
                <span class="text-[10px] font-medium text-purple-600">
                  {{ `${index + 1}/${steps.length}` }}
                </span>
                <div class="flex space-x-1">
                  <button
                    class="inline-flex items-center rounded bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-700
                      hover:bg-gray-100 focus:outline-none"
                    @click="handleExit"
                  >
                    ✕
                  </button>
                  <template v-if="!isFirst">
                    <button
                      class="inline-flex items-center rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-700
                        hover:bg-purple-100 focus:outline-none"
                      @click="previous"
                    >
                      ←
                    </button>
                  </template>
                  <button
                    class="inline-flex items-center rounded bg-purple-600 px-1.5 py-0.5 text-[10px] font-medium text-white
                      hover:bg-purple-700 focus:outline-none"
                    @click="next"
                  >
                    {{ isLast ? '✓' : '→' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VOnboardingStep>
    </template>
  </VOnboardingWrapper>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { VOnboardingWrapper, VOnboardingStep, useVOnboarding } from 'v-onboarding'
  import 'v-onboarding/dist/style.css'
  import { useRouter } from 'vue-router'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { ROUTES } from '@/router/routes'

  const props = defineProps<{
    forceShow?: boolean
  }>()

  const ONBOARDING_COMPLETED_KEY = 'case-detail-onboarding-completed'

  const wrapper = ref(null)
  const router = useRouter()
  const caseStore = useCaseStore()
  const caseRecord = computed(() => caseStore.caseRecord)

  const markOnboardingAsCompleted = () => {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true')
  }

  const hasCompletedOnboarding = () => {
    return !props.forceShow && localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true'
  }

  onMounted(() => {
    if (hasCompletedOnboarding()) {
      return
    }

    // Ensure wrapper is mounted before starting
    if (wrapper.value) {
      console.log('Onboarding component mounted')
      // Debug: Check if target elements exist
      steps.forEach((step, index) => {
        const element = document.querySelector(step.attachTo.element)
        console.log(`Step ${index + 1} target element exists:`, !!element)
      })
      start()
    }
  })

  const getRouteForStep = (stepIndex: number) => {
    const uuid = caseRecord.value?.uuid
    if (!uuid) return null

    switch (stepIndex) {
      case 0:
        return ROUTES.APP.CASES.CASE_DETAIL.OVERVIEW.replace(':uuid', uuid)
      case 1:
        return ROUTES.APP.CASES.CASE_DETAIL.ENTITIES.replace(':uuid', uuid).replace(
          '/:entityUuid?',
          ''
        )
      case 2:
        return ROUTES.APP.CASES.CASE_DETAIL.DOCUMENTS.replace(':uuid', uuid)
      case 3:
        return ROUTES.APP.CASES.CASE_DETAIL.COMMUNICATION.replace(':uuid', uuid)
      case 4:
        return ROUTES.APP.CASES.CASE_DETAIL.APPOINTMENTS.replace(':uuid', uuid)
      case 5:
        return ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(':uuid', uuid)
      case 6:
        return ROUTES.APP.CASES.CASE_DETAIL.TIMELINE.replace(':uuid', uuid)
      case 7:
        return ROUTES.APP.CASES.CASE_DETAIL.AI_CHAT.replace(':uuid', uuid)
      default:
        return null
    }
  }

  const onStepChange = (step: any) => {
    console.log('Step changed:', step)
    const route = getRouteForStep(step.currentStepIndex)
    if (route) {
      router.push(route)
    }
  }

  const steps = [
    {
      attachTo: {
        element: '[data-tab="allgemein"]',
        classList: ['onboarding-highlight']
      },
      content: {
        title: 'Overview',
        description: 'Your redesigned case overview with improved organization and features.'
      }
    },
    {
      attachTo: {
        element: '[data-tab="entities"]',
        classList: ['onboarding-highlight']
      },
      content: {
        title: 'Beteiligte',
        description: 'Manage all case participants efficiently in one place.'
      }
    },
    {
      attachTo: {
        element: '[data-tab="communication"]',
        classList: ['onboarding-highlight']
      },
      content: {
        title: 'Communication',
        description: 'Handle all case communications in one central hub.'
      }
    },
    {
      attachTo: {
        element: '[data-tab="documents"]',
        classList: ['onboarding-highlight']
      },
      content: {
        title: 'Documents',
        description: 'Access and manage all case-related documents.'
      }
    },

    {
      attachTo: {
        element: '[data-tab="appointments"]',
        classList: ['onboarding-highlight']
      },
      content: {
        title: 'Appointments',
        description: 'Track appointments and important deadlines.'
      }
    },
    {
      attachTo: {
        element: '[data-tab="law-specific-data"]',
        classList: ['onboarding-highlight']
      },
      content: {
        title: 'Legal Data',
        description: 'Access law-specific information and requirements.'
      }
    },
    {
      attachTo: {
        element: '[data-tab="timeline"]',
        classList: ['onboarding-highlight']
      },
      content: {
        title: 'Timeline',
        description: 'View the chronological history of your case.'
      }
    },
    {
      attachTo: {
        element: '[data-tab="ai-chat"]',
        classList: ['onboarding-highlight']
      },
      content: {
        title: 'AI Chat',
        description: 'Get intelligent assistance for your case.'
      }
    }
  ]

  const { start, goToStep, finish } = useVOnboarding(wrapper)

  const handleExit = () => {
    markOnboardingAsCompleted()
    emit('exit')
  }
  const emit = defineEmits(['exit', 'finish'])

  // Expose methods for parent component
  defineExpose({
    start: () => {
      console.log('Start onboarding called')
      try {
        console.log('Starting onboarding...')
        start()
        console.log('Onboarding started successfully')
      } catch (error) {
        console.error('Failed to start onboarding:', error)
      }
    },
    goToStep,
    finish
  })
</script>

<style>
  :root {
    --v-onboarding-overlay-z: 9999;
    --v-onboarding-step-z: 10000;
    --v-onboarding-overlay-opacity: 0.2;
    --v-onboarding-step-arrow-size: 8px;
    --v-onboarding-step-arrow-background: white;
  }

  /* Debug styles to make sure elements are visible */
  .v-onboarding-container {
    display: block !important;
    opacity: 1 !important;
    visibility: visible !important;
  }

  .v-onboarding-step {
    display: block !important;
    opacity: 1 !important;
    visibility: visible !important;
  }

  .v-onboarding-overlay {
    display: block !important;
    opacity: 0.2 !important;
    visibility: visible !important;
  }
</style>

<style scoped>
  .onboarding-highlight {
    box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.2) !important;
    border-radius: 0.375rem;
    transition: all 0.2s ease;
  }

  :deep(.v-onboarding-container) {
    z-index: var(--v-onboarding-step-z);
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    pointer-events: none;
  }

  :deep(.v-onboarding-container .v-onboarding-step) {
    padding: 0 !important;
    pointer-events: auto;
    position: absolute !important;
  }

  :deep(.v-onboarding-overlay) {
    z-index: var(--v-onboarding-overlay-z);
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }

  :deep(.v-onboarding-container .v-onboarding-step[data-popper-placement^='top']) {
    margin-bottom: 10px;
  }

  :deep(.v-onboarding-container .v-onboarding-step[data-popper-placement^='bottom']) {
    margin-top: 10px;
  }

  :deep(.v-onboarding-container .v-onboarding-step[data-popper-placement^='left']) {
    margin-right: 10px;
  }

  :deep(.v-onboarding-container .v-onboarding-step[data-popper-placement^='right']) {
    margin-left: 10px;
  }
</style>
